import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { CheckCircle2, Clock, AlertTriangle, FileCheck, Send, Plus } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  draft: { tone: "warning", icon: Clock, label: "Draft" },
  in_progress: { tone: "warning", icon: Clock, label: "In Progress" },
  submitted: { tone: "lime", icon: Send, label: "Submitted" },
  approved: { tone: "success", icon: CheckCircle2, label: "Approved" },
  not_applicable: { tone: "outline", icon: AlertTriangle, label: "N/A" },
};

export default function Filings() {
  const [channels, setChannels] = useState([]);
  const [filings, setFilings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel_code: "SGX-IDEX", fiscal_year: 2024 });

  const load = async () => {
    const [c, f] = await Promise.all([
      api.get("/filings/channels"),
      api.get("/filings"),
    ]);
    setChannels(c.data.channels);
    setFilings(f.data.filings);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/filings", { ...form, fiscal_year: parseInt(form.fiscal_year) });
      toast.success("Filing draft created");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async (id) => {
    try {
      await api.post(`/filings/${id}/action`, { action: "submit" });
      toast.success("SIMULATED submission — channel will return a status update");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const updateProgress = async (id, pct) => {
    try {
      await api.post(`/filings/${id}/action`, { action: "update_progress", progress_pct: pct });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Submissions & Outbound Filings</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Disclosure Filings</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            SGX iDEX · CDP · SBTi · EU CSRD · NEA CPA — track filing status across regulators.{" "}
            <Badge variant="warning" className="ml-1">SIMULATED — Demo Mode</Badge>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="primary" data-testid="new-filing-btn"><Plus className="h-4 w-4" /> New filing</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create filing draft</DialogTitle></DialogHeader>
            <div>
              <Label>Channel</Label>
              <Select value={form.channel_code} onValueChange={(v) => setForm({...form, channel_code: v})}>
                <SelectTrigger data-testid="filing-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {channels.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Fiscal year</Label><Input type="number" value={form.fiscal_year} onChange={(e) => setForm({...form, fiscal_year: e.target.value})} data-testid="filing-year-input" /></div>
            <Button variant="primary" onClick={create} data-testid="filing-create-btn">Create draft</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {filings.map((f) => {
          const s = STATUS_META[f.status] || STATUS_META.draft;
          const SI = s.icon;
          return (
            <div key={f.id} className="card-flat p-5" data-testid={`filing-${f.channel_code}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FileCheck className="h-4 w-4 text-accent" />
                    <h4 className="font-display font-semibold text-lg">{f.channel?.name || f.channel_code}</h4>
                    <Badge variant={s.tone}><SI className="h-3 w-3 mr-1" /> {s.label}</Badge>
                    {f.simulated && <Badge variant="outline" className="text-[10px]">SIMULATED</Badge>}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground space-x-3">
                    <span>FY{f.fiscal_year}</span>
                    {f.channel?.regulator && <span>· {f.channel.regulator}</span>}
                    {f.channel?.format && <span>· {f.channel.format}</span>}
                    {f.due_at && <span>· Due {new Date(f.due_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {f.status !== "submitted" && f.status !== "approved" && f.status !== "not_applicable" && (
                    <Button variant="primary" size="sm" onClick={() => submit(f.id)} data-testid="submit-filing-btn"><Send className="h-3.5 w-3.5" /> Submit</Button>
                  )}
                  {f.score && <Badge variant="lime">Score: {f.score}</Badge>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="md:col-span-2">
                  <div className="flex justify-between text-xs font-mono mb-1.5">
                    <span className="text-muted-foreground">Progress</span>
                    <span>{f.progress_pct}%</span>
                  </div>
                  <Progress value={f.progress_pct} />
                  {f.status === "draft" || f.status === "in_progress" ? (
                    <div className="flex gap-1 mt-2">
                      {[25, 50, 75].map((p) => (
                        <Button key={p} variant="ghost" size="sm" className="text-[10px] h-6" onClick={() => updateProgress(f.id, p)}>Set {p}%</Button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="text-xs font-mono space-y-1">
                  {f.submitted_at && <div><span className="text-muted-foreground">Submitted:</span> {new Date(f.submitted_at).toLocaleDateString()}</div>}
                  {f.validated_at && <div><span className="text-muted-foreground">Validated:</span> {new Date(f.validated_at).toLocaleDateString()}</div>}
                  {f.validator && <div><span className="text-muted-foreground">Verifier:</span> {f.validator}</div>}
                  {f.score_description && <div className="italic text-muted-foreground">{f.score_description}</div>}
                  {f.notes && <div className="italic text-muted-foreground">{f.notes}</div>}
                </div>
              </div>
            </div>
          );
        })}
        {filings.length === 0 && <div className="card-flat p-12 text-center text-muted-foreground">No filings yet.</div>}
      </div>
    </div>
  );
}
